import { ENUM_ORDER_PROVIDER, ENUM_ITEM_TYPE } from 'utils/enum';
import { ENUM_PAYMENT_METHOD, PaystackAccountType } from './payment.model';
import { OrderSurgeType } from './surge.model';
import { UserType } from './user.model';
import { OrderCsRepType } from './cs-profile.model';
import { OrderAddressType } from './address.model';
import { OrderReviewType } from './review.model';
import { OrderMealType, OrderSingleMealEditType, OrderSingleMealType } from './meal.model';
import { OrderBrandIdsType } from './brand.model';
import { OrderLogType } from './order-log.model';

export type BaseOrderType = {
  id: string;
  user_id: string;
  order_details: OrderDetailsType;
  order_recalled_note: string | null;
  order_recalled_time: string | null;
  created_at: string;
  cs_rep?: OrderCsRepType;
  assigned_rep_id?: string;
  completed: boolean;
  cancelled: boolean;
  logistics_company_cancelled: boolean;
  cancelled_note: string | null;
  finished_order_image?: string | null;
  estimated_delivery_time?: string | null;
  estimated_trip_time_in_seconds?: number | null;
  estimated_frontdesk_time?: string | null;
  estimated_kitchen_time?: string | null;
  estimated_total_time?: string | null;
  estimated_unpaid_time?: string | null;
  rider_id: string | null;
  rider_assigned: boolean;
  paid: boolean;
  order_change: null;
  cancelled_by_id: string | null;
  pickup: boolean | null;
  pickup_arrived: boolean;
  rider_arriving: boolean;
  cash_settled: boolean;
  change: string;
  bank_transfer: boolean;
  pickup_id: string | null;
  is_group_order: boolean;
  group_split: boolean | null;
  first_order: boolean | null;
  group_main_order_id: string | null;
  group_main_group_order_id: string | null;
  rider_assist: boolean;
  rider_assist_ids: [];
  logs: {
    time: string;
    description: string;
  }[];
  order_start_notification: boolean;
  shop_completed_time: string | null;
  deleted: boolean;
  transfer_amount: string;
  split_screen_firestore_details_packaging_doc_id: string | null;
  split_screen_firestore_details_frontdesk_doc_id: string | null;
  split_screen_firestore_details_kitchen_doc_id: string | null;
  kitchen_ready_brand_ids: string[];
  no_of_mealbags_delivered: number;
  no_of_drinks_delivered: number;
  rider_started_time: string | null;
  rider_started: boolean | null;
  rider_arrived_time: string | null;
  rider_arrived: boolean;
  is_failed_trip: boolean;
  failed_trip_details: {
    //
  };
  kitchen_accepted_time: string;
  kitchen_completed_time: string;
  kitchen_dispatched_time: string;
  kitchen_verified_time: string | null;
  kitchen_accepted: boolean;
  kitchen_prepared: boolean;
  kitchen_dispatched: boolean;
  kitchen_cancelled: boolean;
  kitchen_time_elapsed: boolean;
  shop_accepted: boolean;
  shop_prepared: boolean;
  completed_time?: string;
  updated_at: string;
  order_code: string;
  surge?: OrderSurgeType | null;
  order_type_id: string;
  is_hidden: boolean;
  order_total_amount_history: { time: string; total_amount: number }[];
  kitchen_preparation_time_in_minutes: number;
  kitchen_start_order: string;
  scheduled: boolean;
  scheduled_delivery_time: string | null;
  scheduled_delivery_date: string | null;
  scheduled_delivery_datetime: string | null;
  recurring_calculated_order_id: string | null;
  preorder: boolean;
  preorder_delivery_date: string | null;
  preorder_delivery_time: string | null;
  preorder_delivery_datetime: string | null;
  rider_wait_time_extended: boolean;
  recurring: boolean;
  point_used: number;
  discount_amount_used: string;
  kitchen_confirmed_time: string | null;
  kitchen_prepared_time: string | null;
  packaging_split_screen_id: string | null;
  kitchen_ready_brand_time: { id: string; completed_time: string }[];
  confirmed_by_id: string | null;
  completed_by_id: string | null;
  shelf_id: string | null;
  box_number: string | null;
  kitchen_split_screen_ids: string | null;
  kitchen_split_screen_brands: Record<string, string[]>;
  company_id: string | null;
  event_id: string | null;
  average_completed_time: string | null;
  report_rider_note: string | null;
  calculated_order_id: string | null;
  cancelled_time: string | null;
  split_screen_firestore_details_packaging_screen: string;
  delivery: ENUM_ORDER_PROVIDER;
  kitchen_confirmed: boolean;
  delivery_time: string | null;
  order_recalled: boolean;
  recalled_by_id: string | null;
  delivery_metadata: Record<string, unknown>;
  fcb_batch_id: string | null;
  user_card_id: string | null;
  notification_late_order_alerts: string[];
  unpaid_time_elapsed: boolean;
  pop_up: boolean;
};

export type SubOrderType = {
  first_order?: boolean;
  total_amount: string;
  meals: OrderMealType[];
  user: UserType;
};

export type OrderType = BaseOrderType & {
  calculated_order: CalculatedOrderType;
  order_type: {
    id: string;
    created_at: string;
    updated_at: string;
    name: ENUM_PAYMENT_METHOD;
  };
  user: UserType;
  rider: UserType;
  order_logs: OrderLogType[];
  order_review: OrderReviewType | null;
};

export type OrderDetailsType = {
  total_wallet_amount: string;
  cutlery: boolean;
  wallets: string[];
  use_bani?: boolean;
  use_wallet?: boolean;
  bank_transfer?: boolean;
  wallet_amount: string;
  paystack_account_details?: PaystackAccountType;
  requires_confirmation_code: boolean;
  foodcourt_confirmation_code: string;
  rider_note?: string;
  partner_rider_name: string;
  relay_delivery_pin: string;
  delivery_handled_by: string;
  partner_rider_phone_number: string;
  partner_delivery_id: string;
};

export type CalculatedOrderType = {
  id: string;
  created_at: string;
  updated_at: string;
  total_amount: string;
  free_delivery: boolean;
  delivery_fee: string;
  prev_delivery_fee: string;
  service_charge: string;
  address_details: OrderAddressType;
  meals: OrderMealType[];
  lat: string;
  lng: string;
  deal_id: string | null;
  cokitchen_polygon_id: string;
  user_id: string;
  cokitchen_id: string;
  referral_code_id: string | null;
  owner_referral_code_id: string | null;
  referral_used: boolean;
  pickup: boolean;
  prev_price: string;
  discount_code: string | null;
  is_group_order: boolean;
  group_order_id: string | null;
  sub_calculated_order_ids: SubOrderType[];
  group_completed: string | null;
  group_split: string | null;
  group_start: string | null;
  group_spend_limit: string | null;
  group_allow_external_invite: string | null;
  group_cancelled: string | null;
  group_locked: string | null;
  group_total_amount: null;
  group_split_paid: null;
  group_host_ready: null;
  group_guests_need_time: string[];
  group_guests_removed_user_ids: string[];
  group_sub_order: string | null;
  group_main_calculated_order_id: string | null;
  group_guests_notify: string[];
  group_guests_completed: boolean;
  group_host_should_remove: string[];
  rider_assist: boolean;
  group_order_host_total_amount: string | null;
  group_guests_need_time_again: string[];
  item_type: ENUM_ITEM_TYPE;
  deleted: boolean;
  logistics_free_delivery: boolean;
  surge: string;
  brand_ids: OrderBrandIdsType[];
  delivery_service_charge: string;
  prev_delivery_service_charge: string;
  pending_amount: string;
  internal_profit: string;
  external_profits: Record<string, unknown>;
  posist_meals: { id: string; addons: { id: string; quantity: number }[]; quantity: number }[];
  is_company_group_order: boolean;
  company_id: string | null;
  preorder_delivery_date: string | null;
  preorder_delivery_time: string | null;
  preorder: boolean;
  preorder_duration_in_hours: string | null;
  scheduled: boolean;
  scheduled_delivery_time: string | null;
  scheduled_delivery_date: string | null;
  recurring: boolean;
  recurring_calculated_order_id: string | null;
  recurring_order_end_date: string | null;
  recurrence: string | null;
  selected_days: [];
  recurring_total_amount: string | null;
  vat_charge: string;
  fcb_batch_order: string | null;
  batch_order_date: string | null;
  company_order: boolean;
  company_user_ids: string[];
  blacklisted_company_user_ids: string[];
  referral_id: string | null;
  user: UserType;
  gift_order_details: string | null;
  appreciation_note: string | null;
  is_bulk_order: boolean;
};

export type OrderBrandMeal = {
  id: string;
  brandName: string;
  amount: number;
  quantity: number;
  meals: OrderSingleMealType[];
};

export type UpdateOrderBrandMeal = {
  id: string;
  brandName: string;
  amount: number;
  quantity: number;
  meals: OrderSingleMealEditType[];
};

export type SubOrderBrandMeals = SubOrderType & {
  brandMeals: OrderBrandMeal[];
};

export class OrderBrandMeals {
  private order?: OrderType;

  constructor(order?: OrderType) {
    this.order = order;
  }

  get meals(): OrderBrandMeal[] {
    if (!this.order || this.order?.calculated_order?.meals?.length === 0) return [];

    return this.formatOrderMeals(this.order.calculated_order.meals);
  }

  get subOrders(): SubOrderBrandMeals[] {
    if (!this.order || this.order?.calculated_order?.sub_calculated_order_ids?.length === 0)
      return [];

    const subOrders = this.order.calculated_order.sub_calculated_order_ids;

    return subOrders.map((subOrder) => ({
      ...subOrder,
      brandMeals: this.formatOrderMeals(subOrder.meals)
    }));
  }

  private formatOrderMeals(brandMeals: OrderMealType[]) {
    return brandMeals.map((orderMeal) => ({
      id: orderMeal.brand.id,
      brandName: orderMeal.brand.name,
      amount: orderMeal.amount,
      quantity: 1,
      meals: (orderMeal.meals ?? []).map((meal) => ({
        ...meal,
        addons: (meal.addons ?? []).map((addon) => ({
          ...addon,
          name: addon.name ?? addon.meal_data.name
        }))
      }))
    }));
  }
}
